// Global code
if (!String.prototype.replaceAll) {
    String.prototype.replaceAll = function (str, newStr) {

        // If a regex pattern
        if (Object.prototype.toString.call(str).toLowerCase() === '[object regexp]') {
            return this.replace(str, newStr);
        }

        // If a string
        return this.replace(new RegExp(str, 'g'), newStr);

    };
}

if (!__DEV__) {
    console.log = () => { };
}

// React && Own-lib

// Providers
import { LanguageProvider } from '@languageProvider';
import ThemeProvider, { useTheme } from '@themeProvider';

// Native libs
// import * as Font from 'expo-font';

import { ModalProvider, SnackbarProvider } from '@lib';
import { setDeviceDimensions } from '@styles/commons';

import { Commons, Spacing, Typography } from '@styles';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Animated, BackHandler, SafeAreaView, ScrollView, Text, View } from 'react-native';
// import FesteaPay from '@assets/images/FesteaPay';
import { Button } from '@atoms';
import { Cart, CartSelection, ProductNode, ProductPack, Promotion, SelectionTree } from '@datamodel';
import { useModal } from '@lib';
import { AddItemModal, AddPackModal } from '@modals';
import { FungibleCard, ProductPackCard } from '@molecules';
import Animation from '@utils/animations';
import { objectFilter, objectMap } from '@utils/helpers';

const styles = Commons.styles;

const INITIAL_SERVER_CART = {
    selections: [],
    promotion_codes: [],
}

const StatefulApp = (props) => {
    // MenuScreen props
    const menu = props.menu;
    const promotions = {};
    const event = null;

    // TODO: Add pack support
    const packs = {};

    // If menu is empty object
    if (Object.keys(menu).length === 0) {
        return <></>
    }


    const { useColor, setColors } = useTheme();
    const { pushModal } = useModal();

    const [colorsReady, setColorsReady] = useState(false);

    const [backgroundColor, body, line, inputBackground, label, secondary, primaryText] = useColor([
        'background', 'body', 'line', 'inputBackground', 'label', 'secondary', ['primary', { isText: true }]
    ]);

    useEffect(() => {
        if (props.palette) {
            setColors(JSON.parse(props.palette));
        }
        setColorsReady(true);
    }, [colorsReady]);

    const totalPacks = useRef(0);
    const rightDrawerAnim = useRef(new Animated.Value(0)).current;
    const leftDrawerAnim = useRef(new Animated.Value(0)).current;

    const [serverCart, setServerCart] = useState(INITIAL_SERVER_CART);

    const areas = useMemo(() => ProductNode.formatAreas(ProductNode.standaloneProducts(menu)), [menu]);

    const usedPromotions = useMemo(() => Cart.findPromotionUses(Cart.parse(serverCart)), [serverCart]);
    const tempPromotions = useMemo(() => objectFilter(Promotion.updatePromotionUses(JSON.parse(JSON.stringify(promotions)), usedPromotions), p => Promotion.maxUses(p) > 0), [promotions, usedPromotions]);
    const { coupons, offers } = useMemo(() => Promotion.collectionSplitByType(tempPromotions), [tempPromotions]);

    const stackedCart = useMemo(() => {
        return Cart.stackSelections(Cart.addSelectionIds(Cart.parse(serverCart)));
    }, [serverCart]);

    const cartCount = useMemo(() => {
        let total = 0;
        let grouped = {};

        const addToGrouped = (id, qty = 1) => {
            if (id in grouped) {
                grouped[id] += qty;
            } else {
                grouped[id] = qty;
            }
        }

        Object.keys(stackedCart.selections).forEach(selId => {
            const sel = stackedCart.selections[selId];
            addToGrouped(CartSelection.getAreaMainSelection(sel, areas), sel.quantity);
            total += sel.quantity;
        })

        Object.keys(stackedCart.packs).forEach(pIndex => {
            const pack = stackedCart.packs[pIndex];
            addToGrouped(pack.packId, pack.quantity);
            total += pack.quantity * pack.length;
        });

        return { total, grouped };
    }, [stackedCart]);

    const addToCartPress = (serverItem, { quantity, selectedPromotions = {} }) => {
        const selections = serverCart.selections;

        const nItems = serverItem.length || 1;
        // serverItem.item.promotion_codes = promotionIds;

        const addSelections = Array.from({ length: quantity }, _ => JSON.parse(JSON.stringify(serverItem))).flat();

        // Add promotions
        Object.keys(selectedPromotions).forEach(promoId => {
            const { quantity } = selectedPromotions[promoId];
            addSelections.slice(0, quantity).forEach(sItem => {
                sItem.item.promotion_codes.push(promoId);
            })
        })

        let isPackItem = false;
        addSelections.forEach(sel => {
            if (sel.item.pack_item) {
                sel._pack_index = totalPacks.current;
                sel._pack_length = nItems;
                isPackItem = true;
            }
        });

        if (isPackItem) totalPacks.current++;

        setServerCart({
            ...serverCart,
            selections: [...selections, ...addSelections],
        })
    };

    const displayItemModal = useMemo(() => AddItemModal.display({
        addToCartPress,
        coupons,
        offers,
        showStepper: true,
    }), [addToCartPress, coupons, offers]);

    const productCardPress = (fungibleParent) => (option) => {
        const selectionTree = SelectionTree.init(fungibleParent);
        const nodeId = option.nodeId;
        SelectionTree.mark(selectionTree, { nodeId, value: true });

        const availableOffers = offers.products[fungibleParent.nodeId];
        const availableCoupons = coupons.products[fungibleParent.nodeId];
        const nCoupons = availableCoupons ? Object.keys(nCoupons).length : 0;

        const price = SelectionTree.calculatePrice(selectionTree);
        const {
            promotionId: appliedOffer,
        } = Promotion.calculateBestOffer(availableOffers || {}, price);

        const selectedPromotions = {};
        if (appliedOffer) {
            selectedPromotions[appliedOffer] = {
                isOffer: true,
                quantity: 1
            }
        }

        if (
            // Selection tree is full
            selectionTree.marked && selectionTree.solved && selectionTree.valid &&
            // There is no coupon selection
            nCoupons < 1
        ) {
            addToCartPress(SelectionTree.toServer(SelectionTree.addMainSelection(selectionTree, fungibleParent)), { quantity: 1, selectedPromotions });

        } else {
            displayItemModal(fungibleParent)(option, selectionTree);
        }

    }

    const packCardPress = (pack) => {
        const packAreas = ProductPack.getModalAreas(pack);
        const initialSelections = ProductPack.getInitialSelections(pack, packAreas);
        const isComplete = initialSelections.every(s => SelectionTree.isComplete(s));
        const itemIdByProductId = objectMap(ProductPack.getProductIds(pack, { repetitions: false, sorted: false }), 'productId')

        if (isComplete) {
            addToCartPress(initialSelections.map(s => SelectionTree.toServer(s, { itemIdMap: itemIdByProductId, packId: pack.packId })), { quantity: 1 });
            return;
        }

        pushModal({
            children: <AddPackModal
                style={{ maxHeight: Commons.dHeight - Spacing.NAVBAR * 2, width: Commons.dWidth - Spacing.MARGINS * 2 }}
                pack={pack}
                initialSelections={initialSelections}
                addToCartPress={addToCartPress}
                itemIdByProductId={itemIdByProductId}
                areas={packAreas}
            />,
            style: {
                justifyContent: 'center',
                alignItems: 'center',
            }
        })

    }

    const setSelectionQuantity = (_selId) => (quantity) => {

        const cart = Cart.unstackSelection({
            ...stackedCart,
            selections: {
                ...stackedCart.selections,
                [_selId]: {
                    ...stackedCart.selections[_selId],
                    quantity
                }
            },
        });

        setServerCart(Cart.toServer({
            ...cart,
            selections: cart.selections
        }));
    }


    const setPackQuantity = (_packIndex, cartPack) => (quantity) => {
        const cart = Cart.parse(serverCart);

        const selections = [];

        // Add those which are not in the pack
        cart.selections.forEach(s => {
            if (s._packIndex != _packIndex) {
                selections.push(s)
            }
        })

        // Add the pack ones, unrolling them first
        const singlePack = Object.keys(cartPack.selections).flatMap(_selId => {
            const itemSel = cartPack.selections[_selId];
            return new Array(itemSel.quantity).fill(itemSel)
        })

        const packSelections = (new Array(quantity).fill(singlePack)).flat();
        selections.push(...packSelections);

        setServerCart(Cart.toServer({ ...cart, selections }));

    }

    const closeDrawer = {
        right: () => {
            Animated.timing(rightDrawerAnim, Animation.short(0)).start();
        },
        left: () => {
            Animated.timing(leftDrawerAnim, Animation.short(0)).start();
        }
    }

    useEffect(() => {
        const backAction = () => {
            closeDrawer.right();
            closeDrawer.left();
        }

        const backHandler = BackHandler.addEventListener(
            "hardwareBackPress",
            backAction
        );

        return () => {
            window.removeEventListener('popstate', backAction);
            backHandler.remove()
        }
    }, []);

    const eventBanner = event ? <View style={{ paddingTop: Spacing.XS, paddingHorizontal: Spacing.MARGINS }}>
        <Text style={[Typography.TITLE, { textAlign: 'center', color: primaryText, fontSize: 14 }]}>Comprando en {event.name}</Text>
    </View> : <></>

    if (!colorsReady) return <></>;

    return <SafeAreaView {...props} style={{ flex: 1, backgroundColor, justifyContent: 'center', paddingTop: Spacing.MARGINS }}>
        <ScrollView
            style={[styles.scrollView]}
            contentContainerStyle={{ paddingHorizontal: Spacing.MARGINS, paddingBottom: Spacing.XL + Button.HEIGHT }}
            showsVerticalScrollIndicator={false}
        >
            {eventBanner}
            {Object.keys(packs).map(packId => {
                return <ProductPackCard
                    cartCount={cartCount.grouped[packId]}
                    onPress={() => packCardPress(packs[packId])}
                    setPackQuantity={setPackQuantity}
                    stackedCart={stackedCart}
                    pack={packs[packId]}
                    key={packId}
                    style={{ marginBottom: Spacing.M }} />
            })}

            {!!areas &&
                areas.filter(area => area.fungibleProducts?.length).map(area => (
                    area.fungibleProducts.map(fp => (
                        <FungibleCard
                            {...Promotion.collectionSplitByAutomatic(Promotion.collectionFilterByProductId(tempPromotions, fp.nodeId))}
                            cartCount={cartCount.grouped}
                            displayModal={productCardPress(fp.node)}
                            key={fp.nodeId}
                            product={fp.node}
                            setSelectionQuantity={setSelectionQuantity}
                            stackedCart={stackedCart}
                            style={{ marginBottom: Spacing.M }}
                            options={fp.options} />
                    ))
                ))
            }
        </ScrollView>
    </SafeAreaView>
}

export function MenuPreview(props) {
    setDeviceDimensions(props.HTMLElement);
    const menuResponse = JSON.parse(props.menu);
    return (
        <LanguageProvider>
            <ThemeProvider>
                <SnackbarProvider>
                    <ModalProvider>
                        <StatefulApp {...props} menu={menuResponse.menu ? ProductNode.parse(menuResponse.menu) : {}} />
                    </ModalProvider>
                </SnackbarProvider>
            </ThemeProvider>
        </LanguageProvider>
    );
}

export default MenuPreview;
